import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useGetAuthUserMutation } from 'entities/user';
import { CommonError } from 'shared/api/types';
import { deleteToken, returnToName, saveToken } from 'shared/lib/axios';
import { LocalStorageService } from 'shared/lib/localStorage';
import { pathKeys } from 'shared/lib/react-router';
import { addToast } from 'shared/model/toast';

import { authStore } from '../model/store';

import { authenticatedUserGet, logIn, logInWithGoogle, logOut } from './requests';
import { AuthFormLoginRequest, AuthResponse, AuthUserAccessDTO } from './types';

const keys = {
  root: () => ['auth'],
  currentUser: () => [...keys.root(), 'currentUser'] as const,
  loginUser: () => [...keys.root(), 'loginUser'] as const,
  logoutUser: () => [...keys.root(), 'logoutUser'] as const,
};

export const useAuthUserMutation = (): UseMutationResult<AuthUserAccessDTO, unknown, void> => {
  const navigate = useNavigate();
  const { mutateAsync: getAuthInfoMutation } = useGetAuthUserMutation();

  return useMutation({
    mutationKey: keys.currentUser(),
    mutationFn: authenticatedUserGet,
    onSuccess: async (data) => {
      const { id, firstName, lastName, email, picture, createdAt, createdBy, updatedAt, updatedBy } =
        await getAuthInfoMutation();
      const username = `${firstName} ${lastName}`;

      authStore
        .getState()
        .updateAuthUser({ ...data, id, username, email, picture, createdAt, createdBy, updatedAt, updatedBy });

      const returnToList = LocalStorageService.getItem<Record<string, string>>(returnToName) ?? {};
      const returnTo = returnToList[id];

      if (returnTo) {
        delete returnToList[id];
      }
      navigate(returnTo ?? pathKeys.dashboard());
    },
  });
};

const logInHandler = async (tokens: AuthResponse): Promise<void> => {
  const { accessToken, refreshToken, refreshTokenExpiredAt } = tokens;

  saveToken(accessToken);

  authStore.getState().updateTokens({ token: accessToken, refreshToken, refreshTokenExpiredAt });
};

export function useLoginUserMutation(): UseMutationResult<AuthResponse, CommonError, AuthFormLoginRequest> {
  const { mutate: getAuthInfo } = useAuthUserMutation();

  return useMutation({
    mutationKey: keys.loginUser(),
    mutationFn: logIn,
    onSuccess: (tokens) => {
      logInHandler(tokens);
      getAuthInfo();
    },
    onError(error) {
      if (error.response?.data.code === 'UserBlockedException') {
        addToast({
          type: 'short',
          method: 'warning',
          description: 'Auth:LogInForm.message.maximumAttempts.description',
        });
      }
    },
  });
}

export function useGoogleLoginUserMutation(): UseMutationResult<AuthResponse, unknown, string> {
  const { mutate: getAuthInfo } = useAuthUserMutation();

  return useMutation({
    mutationKey: keys.loginUser(),
    mutationFn: logInWithGoogle,
    onSuccess: (tokens) => {
      logInHandler(tokens);
      getAuthInfo();
    },
  });
}

export function useLogoutMutation(returnTo?: string): UseMutationResult<void, unknown, void> {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: keys.logoutUser(),
    mutationFn: logOut,
    onSettled: async () => {
      const authState = authStore.getState();

      const id = authState.authUser?.id;
      if (id && returnTo) {
        const previousReturnTo: Record<string, string> = LocalStorageService.getItem(returnToName) ?? {};
        LocalStorageService.setItem(returnToName, JSON.stringify({ ...previousReturnTo, [id]: returnTo }));
      }

      authState.reset();
      queryClient.cancelQueries();
      queryClient.clear();
      deleteToken();
      navigate(pathKeys.login());
    },
  });
}
